import TextEditor from "cpl-react-common/components/Inputs/TextEditor";
import { useSelector } from "react-redux";
import { ITextEditorImageUploadService } from "services/TextEditorImageUploadService";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";
import {
  getStandardDescriptions,
  selectLoading,
  selectStandardDescriptions,
} from "../standardDescriptions/components/StandardDescriptionsSlice";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { createAsyncThunk } from "@reduxjs/toolkit";

type CPLTextEditorProps = {
  "data-test-id"?: string;
  value?: string;
  initialValue?: string;
  disabled?: boolean;
  onChange?: (value: string) => void;
  height?: number;
  maxImageSizeBytes?: number;
  allowImages?: boolean;
  enableAutoComplete?: boolean;
};

const uploadImageThunk = createAsyncThunk<string, File>("TextEditor/UploadImage", async (image: File) => {
  const textEditorImageUploadService = container.get<ITextEditorImageUploadService>(
    SERVICE_TYPES.TextEditorImageUploadService
  );
  return await textEditorImageUploadService.uploadImage(image);
});

const CPLTextEditor: React.FC<CPLTextEditorProps> = ({
  "data-test-id": testId,
  value,
  initialValue,
  disabled = false,
  onChange,
  height,
  maxImageSizeBytes,
  allowImages = true,
  enableAutoComplete = true,
}) => {
  const thunkDispatch = useThunkDispatch();
  const uploadImage = async (image: File) => {
    return thunkDispatch(uploadImageThunk(image)).unwrap();
  };

  const loading = useSelector(selectLoading);
  const standardDescriptions = useSelector(selectStandardDescriptions);

  const updateAutoCompleteOptions = () => {
    thunkDispatch(getStandardDescriptions());
  };

  return (
    <TextEditor
      data-test-id={testId}
      tinymceScriptSrc={process.env.PUBLIC_URL + "/tinymce/tinymce.min.js"}
      uploadImage={allowImages ? uploadImage : undefined}
      onChange={onChange}
      value={value}
      initialValue={initialValue}
      disabled={disabled}
      height={height}
      maxImageSizeBytes={maxImageSizeBytes}
      autoComplete={
        enableAutoComplete
          ? {
              buttonTooltip: "Standard Descriptions",
              autoCompleteOptions: standardDescriptions.map((standardDesc) => ({
                code: standardDesc.code ?? "",
                displayValue: standardDesc.description ?? "",
                selectValue: standardDesc.description ?? "",
              })),
              updateAutoCompleteOptions,
              searchPlaceholder: "Search work items",
              loading,
            }
          : undefined
      }
    />
  );
};

export default CPLTextEditor;
